import {
  Button,
  Card,
  CardContent,
  H2,
  InputCheckbox,
  InputContainer,
} from "@sellar-io/components";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Alert } from "../../../components/alert/Alert";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import {
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING,
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ACCOUNTS,
} from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useRyftManagementTermsOfServiceAccept } from "../../../hooks/api/v2/ryftManagement/useRyftManagementTermsOfServiceAccept";
import { RYFT_TERMS_URL } from "./constants";
import { RyftNonHostedOnboardingStepRedirect } from "./components/RyftNonHostedOnboardingStepRedirect";

export const RyftNonHostedOnboardingTerms = () => {
  return <RyftNonHostedOnboardingTermsLayout />;
};

const RyftNonHostedOnboardingTermsLayout = () => {
  return (
    <RyftNonHostedOnboardingStepRedirect stepId="accept-terms">
      <RyftNonHostedOnboardingTermsContent />
    </RyftNonHostedOnboardingStepRedirect>
  );
};

const RyftNonHostedOnboardingTermsContent = () => {
  const navigate = useNavigate();
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const acceptTermsMutation = useRyftManagementTermsOfServiceAccept();

  const handleAcceptTerms = () => {
    if (!hasAgreedToTerms || acceptTermsMutation.isPending) return;

    acceptTermsMutation.mutate(undefined, {
      onSuccess: () => {
        navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ACCOUNTS, {
          replace: true,
        });
      },
    });
  };

  const errorMessage =
    apiV2AssertError(acceptTermsMutation.error)?.response?.data.message ??
    "Unable to accept Ryft's terms right now";

  return (
    <>
      <NavigationBar
        title="Accept Ryft's terms"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={() =>
          navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING)
        }
      />
      <PageContainer className="mx-auto flex max-w-3xl flex-col gap-6 p-4 md:p-6">
        <header className="flex flex-col gap-2">
          <H2>Accept Ryft's terms</H2>
          <p className="max-w-2xl text-sm leading-6 text-zinc-600 md:text-base">
            Read Ryft's terms and conditions before accepting them on behalf of
            your business.
          </p>
        </header>

        {acceptTermsMutation.isError && (
          <Alert type="error" message={errorMessage} className="w-full" />
        )}

        <Card className="border border-zinc-200 bg-white">
          <CardContent className="flex flex-col gap-6 p-4 md:p-6">
            <div className="flex flex-col gap-2">
              <h3 className="font-semibold text-zinc-900">
                Ryft terms and conditions
              </h3>
              <p className="text-sm leading-6 text-zinc-600">
                Review the full agreement on Ryft's website. It opens in a new
                tab so you can return here when you're ready.
              </p>
              <a
                href={RYFT_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-fit text-sm font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
              >
                Read Ryft's terms and conditions
              </a>
            </div>

            <InputContainer className="w-full border-none bg-zinc-50 p-4">
              <InputCheckbox
                id="ryft-terms-agreement"
                checked={hasAgreedToTerms}
                onChange={(event) => setHasAgreedToTerms(event.target.checked)}
                disabled={acceptTermsMutation.isPending}
                className="flex-none cursor-pointer"
              />
              <label
                htmlFor="ryft-terms-agreement"
                className="min-w-0 flex-1 cursor-pointer text-sm text-zinc-800"
              >
                I have read and agree to Ryft's terms and conditions on behalf
                of my business.
              </label>
            </InputContainer>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            disabled={!hasAgreedToTerms || acceptTermsMutation.isPending}
            loading={acceptTermsMutation.isPending}
            onClick={handleAcceptTerms}
          >
            Accept terms
          </Button>
        </div>
      </PageContainer>
    </>
  );
};
